// ============================================
// MSG (Outlook) BINARY PARSER
// ============================================
// Parses Microsoft Outlook .msg files (OLE2 Compound File Binary Format).
// Extracts: Subject, From, To, CC, Date, and Body (plain text + HTML fallback).
//
// The MSG format is a Microsoft Compound File (CFBF/OLE2) container that holds
// MAPI properties as named streams. Each property is stored in a stream named
// "__substg1.0_XXXXYYYY" where XXXX is the MAPI property tag and YYYY is the
// property type (001F = Unicode string, 001E = ANSI string).
//
// This parser handles the most common email properties without requiring any
// external dependencies. It's designed for the QuoteMaker preprocessing pipeline
// where we only need sender, recipient, subject, date, and body.
//
// Reference: [MS-CFB] and [MS-OXMSG] specifications.

// ─── MAPI property tags we care about ──────────────────────────────────────
const PROP_TAGS = {
    SUBJECT:             0x0037,  // PR_SUBJECT
    SENDER_NAME:         0x0C1A,  // PR_SENDER_NAME
    SENDER_EMAIL:        0x0C1F,  // PR_SENDER_EMAIL_ADDRESS
    SENDER_SMTP:         0x5D01,  // PR_SENDER_SMTP_ADDRESS (fallback)
    DISPLAY_TO:          0x0E04,  // PR_DISPLAY_TO
    DISPLAY_CC:          0x0E03,  // PR_DISPLAY_CC
    CLIENT_SUBMIT_TIME:  0x0039,  // PR_CLIENT_SUBMIT_TIME
    MESSAGE_DELIVERY:    0x0E06,  // PR_MESSAGE_DELIVERY_TIME
    BODY:                0x1000,  // PR_BODY (plain text)
    BODY_HTML:           0x1013,  // PR_BODY_HTML
    SENT_REPRESENTING:   0x0042,  // PR_SENT_REPRESENTING_NAME
    SENT_REP_EMAIL:      0x0065,  // PR_SENT_REPRESENTING_EMAIL_ADDRESS
    SENT_REP_SMTP:       0x5D02,  // PR_SENT_REPRESENTING_SMTP_ADDRESS (real SMTP for delegated sends)
    // Attachment-scoped properties — these live inside __attach_versionX_#XXXXXXXX storages
    ATTACH_FILENAME:     0x3704,  // PR_ATTACH_FILENAME (8.3 short)
    ATTACH_LONG_FILENAME:0x3707,  // PR_ATTACH_LONG_FILENAME
    ATTACH_DATA_BIN:     0x3701,  // PR_ATTACH_DATA_BIN
    ATTACH_MIME_TAG:     0x370E,  // PR_ATTACH_MIME_TAG (Content-Type string)
    ATTACH_CONTENT_ID:   0x3712,  // PR_ATTACH_CONTENT_ID
    ATTACH_METHOD:       0x3705,  // PR_ATTACH_METHOD (1=by value, 5=embedded)
    ATTACH_FLAGS:        0x3714,  // PR_ATTACH_FLAGS (4 = hidden inline)
    DISPLAY_NAME:        0x3001,  // PR_DISPLAY_NAME (used as attachment fallback)
    ATTACH_EXTENSION:    0x3703,  // PR_ATTACH_EXTENSION (".jpg")
};

// Property type suffixes
const PT_UNICODE = 0x001F;
const PT_STRING8 = 0x001E;
const PT_BINARY  = 0x0102;

const NOSTREAM = 0xFFFFFFFF;
const MAX_ATTACHMENTS = 50;

// ─── Structured shapes ──────────────────────────────────────────────────────

// An OLE2 directory entry. `null` placeholders keep array indices aligned with
// directory IDs (free/empty slots), so consumers index `entries[id]` directly.
interface DirEntry {
    name: string;
    type: number;        // 1=storage, 2=stream, 5=root
    leftSib: number;
    rightSib: number;
    child: number;
    startSect: number;
    streamSize: number;
    index: number;
}

// A MAPI property stream resolved from a __substg1.0_XXXXYYYY name.
interface PropStream {
    entry: DirEntry;
    type: number;        // MAPI property type (0x001F unicode, 0x001E ansi, 0x0102 binary)
}

interface MsgHeaders {
    emailSubject?: string;
    emailFrom?: string | null;
    emailTo?: string;
    emailCc?: string;
    emailDate?: string;
}

interface MsgAttachment {
    filename: string;
    mimeType: string;
    size: number;
    isInline: boolean;
    contentId: string;
    dataUrl: string;
    blob: Blob | null;
}

export interface ParsedMsg {
    headers: MsgHeaders;
    body: string;
    bodyPlain: string;
    bodyHtml: string;
    attachments: MsgAttachment[];
}

// OLE2 constants
const HEADER_SIZE = 512;
const DIR_ENTRY_SIZE = 128;
const ENDOFCHAIN = 0xFFFFFFFE;
const FREESECT   = 0xFFFFFFFF;
const HEADER_MAGIC = [0xD0, 0xCF, 0x11, 0xE0, 0xA1, 0xB1, 0x1A, 0xE1];

/**
 * Parse an Outlook .msg file from an ArrayBuffer.
 *
 * @param {ArrayBuffer} buffer - Raw file bytes
 * @returns {{ headers: Object, body: string }}
 */
export function parseMsgBinary(buffer: ArrayBuffer): ParsedMsg {
    const view = new DataView(buffer);
    const bytes = new Uint8Array(buffer);

    // ── Validate OLE2 magic ──────────────────────────────────────────
    for (let i = 0; i < 8; i++) {
        if (bytes[i] !== HEADER_MAGIC[i]) {
            throw new Error('Not a valid OLE2/MSG file (bad magic bytes)');
        }
    }

    // ── Read header ──────────────────────────────────────────────────
    const sectorSizePow  = view.getUint16(30, true);
    const sectorSize     = 1 << sectorSizePow;           // usually 512
    const miniSizePow    = view.getUint16(32, true);
    const miniSectorSize = 1 << miniSizePow;              // usually 64
    const fatSectors     = view.getUint32(44, true);      // total FAT sectors
    const dirStart       = view.getUint32(48, true);      // first directory sector
    const miniCutoff     = view.getUint32(56, true);      // mini-stream cutoff (4096)
    const miniFatStart   = view.getUint32(60, true);      // first mini-FAT sector
    const miniFatCount   = view.getUint32(64, true);      // total mini-FAT sectors
    const difatStart     = view.getUint32(68, true);      // first DIFAT sector
    const difatCount     = view.getUint32(72, true);      // total DIFAT sectors

    // ── Build FAT (sector allocation table) ──────────────────────────
    // First 109 DIFAT entries are in the header at offset 76
    const difatEntries: number[] = [];
    for (let i = 0; i < 109; i++) {
        const s = view.getUint32(76 + i * 4, true);
        if (s !== FREESECT && s !== ENDOFCHAIN) difatEntries.push(s);
    }

    // If there are more than 109 FAT sectors, follow the DIFAT chain
    if (difatCount > 0 && difatStart !== ENDOFCHAIN) {
        let difatSect = difatStart;
        for (let d = 0; d < difatCount && difatSect !== ENDOFCHAIN; d++) {
            const offset = HEADER_SIZE + difatSect * sectorSize;
            const entriesPerSect = (sectorSize / 4) - 1; // last entry is chain pointer
            for (let i = 0; i < entriesPerSect; i++) {
                const s = view.getUint32(offset + i * 4, true);
                if (s !== FREESECT && s !== ENDOFCHAIN) difatEntries.push(s);
            }
            difatSect = view.getUint32(offset + entriesPerSect * 4, true);
        }
    }

    // Read FAT entries from each FAT sector
    const fat: number[] = [];
    for (const fatSect of difatEntries) {
        const offset = HEADER_SIZE + fatSect * sectorSize;
        for (let i = 0; i < sectorSize / 4; i++) {
            fat.push(view.getUint32(offset + i * 4, true));
        }
    }

    /**
     * Follow a FAT chain starting from `startSect` and return concatenated sector data.
     */
    function readStream(startSect: number, streamSize: number): Uint8Array {
        const chunks: Uint8Array[] = [];
        // `sect` is only ever undefined right before the loop breaks (the FAT
        // lookup below), so the `!` uses inside the body hold by the loop
        // invariant; they are compile-erased.
        let sect: number | undefined = startSect;
        let remaining = streamSize;
        let safety = 0;
        while (sect !== ENDOFCHAIN && remaining > 0 && safety++ < 100000) {
            const offset = HEADER_SIZE + sect! * sectorSize;
            const chunkLen = Math.min(sectorSize, remaining);
            if (offset + chunkLen > buffer.byteLength) break;
            chunks.push(new Uint8Array(buffer, offset, chunkLen));
            remaining -= chunkLen;
            sect = fat[sect!];
            if (sect === undefined || sect === FREESECT) break;
        }
        const result = new Uint8Array(streamSize);
        let pos = 0;
        for (const chunk of chunks) {
            result.set(chunk, pos);
            pos += chunk.length;
        }
        return result;
    }

    // ── Read directory entries ────────────────────────────────────────
    // Each entry carries Red-Black tree links so a Storage's children can
    // be enumerated by walking from its `child` ID, then in-order through
    // left/right siblings. `index` is the entry's directory ID.
    const dirData = readStream(dirStart, fat.length * sectorSize); // over-read is fine
    const entries: (DirEntry | null)[] = [];
    const maxEntries = Math.floor(dirData.length / DIR_ENTRY_SIZE);

    // All byte reads below are in-bounds: `off` ranges over [0, maxEntries*128)
    // and maxEntries = floor(dirData.length / 128), so off + <=120 stays within
    // dirData. The `!` assertions reflect that invariant and are compile-erased.
    function readU32(buf: Uint8Array, off: number): number {
        return (buf[off]! | (buf[off + 1]! << 8) | (buf[off + 2]! << 16) | (buf[off + 3]! << 24)) >>> 0;
    }

    for (let i = 0; i < maxEntries; i++) {
        const off = i * DIR_ENTRY_SIZE;
        const nameLen = dirData[off + 64]! | (dirData[off + 65]! << 8);
        if (nameLen === 0) {
            entries.push(null); // keep IDs aligned with array indices
            continue;
        }

        // Entry name is UTF-16LE, nameLen includes the null terminator (in bytes)
        const nameBytes = Math.min(nameLen - 2, 62); // exclude null terminator
        let name = '';
        for (let j = 0; j < nameBytes; j += 2) {
            name += String.fromCharCode(dirData[off + j]! | (dirData[off + j + 1]! << 8));
        }

        const type       = dirData[off + 66]!; // 1=storage, 2=stream, 5=root
        const leftSib    = readU32(dirData, off + 68);
        const rightSib   = readU32(dirData, off + 72);
        const child      = readU32(dirData, off + 76);
        const startSect  = readU32(dirData, off + 116);
        const streamSize = readU32(dirData, off + 120);

        entries.push({ name, type, leftSib, rightSib, child, startSect, streamSize, index: i });
    }

    // In-order walk of the Red-Black tree under a given storage entry.
    // Returns an array of child entries (storages + streams) directly
    // contained by `parent`.
    function listChildrenOf(parent: DirEntry | null | undefined): DirEntry[] {
        if (!parent || parent.child === NOSTREAM) return [];
        const out: DirEntry[] = [];
        const visited = new Set<number>();
        function walk(id: number): void {
            if (id === NOSTREAM) return;
            if (visited.has(id)) return;
            visited.add(id);
            if (id < 0 || id >= entries.length) return;
            const e = entries[id];
            if (!e) return;
            walk(e.leftSib);
            out.push(e);
            walk(e.rightSib);
        }
        walk(parent.child);
        return out;
    }

    // ── Build mini-stream (for streams < miniCutoff) ─────────────────
    const rootEntry = entries.find(e => e && e.type === 5);
    let miniStreamData: Uint8Array | null = null;
    let miniFat: number[] = [];

    if (rootEntry && rootEntry.streamSize > 0) {
        miniStreamData = readStream(rootEntry.startSect, rootEntry.streamSize);
    }

    if (miniFatStart !== ENDOFCHAIN && miniFatCount > 0) {
        const miniFatData = readStream(miniFatStart, miniFatCount * sectorSize);
        const miniFatView = new DataView(miniFatData.buffer, miniFatData.byteOffset, miniFatData.byteLength);
        for (let i = 0; i < miniFatData.length / 4; i++) {
            miniFat.push(miniFatView.getUint32(i * 4, true));
        }
    }

    /**
     * Read a mini-stream chain.
     */
    function readMiniStream(startSect: number, streamSize: number): Uint8Array {
        if (!miniStreamData) return new Uint8Array(0);
        const chunks: Uint8Array[] = [];
        // Same loop-invariant as readStream: `sect` is only undefined right
        // before a break, so the `!` uses inside are compile-erased.
        let sect: number | undefined = startSect;
        let remaining = streamSize;
        let safety = 0;
        while (sect !== ENDOFCHAIN && remaining > 0 && safety++ < 100000) {
            const offset = sect! * miniSectorSize;
            const chunkLen = Math.min(miniSectorSize, remaining);
            if (offset + chunkLen > miniStreamData.length) break;
            chunks.push(miniStreamData.slice(offset, offset + chunkLen));
            remaining -= chunkLen;
            sect = miniFat[sect!];
            if (sect === undefined || sect === FREESECT) break;
        }
        const result = new Uint8Array(streamSize);
        let pos = 0;
        for (const chunk of chunks) {
            result.set(chunk, pos);
            pos += chunk.length;
        }
        return result;
    }

    /**
     * Read an entry's data - routes to mini-stream or regular stream.
     */
    function readEntryData(entry: DirEntry): Uint8Array {
        if (entry.streamSize === 0) return new Uint8Array(0);
        if (entry.streamSize < miniCutoff && miniStreamData) {
            return readMiniStream(entry.startSect, entry.streamSize);
        }
        return readStream(entry.startSect, entry.streamSize);
    }

    // ── Extract MAPI properties ──────────────────────────────────────
    // Stream names follow the pattern: __substg1.0_XXXXYYYY
    // where XXXX = property tag (hex), YYYY = property type (hex).
    //
    // We build a propStreams map per storage (top-level message AND each
    // attachment's substorage), so the same readers work for both.
    function buildPropMapFromList(childList: (DirEntry | null)[]): Map<number, PropStream> {
        const map = new Map<number, PropStream>();
        for (const e of childList) {
            if (!e) continue;
            const m = e.name.match(/__substg1\.0_([0-9A-Fa-f]{4})([0-9A-Fa-f]{4})/);
            if (!m) continue;
            const tag = parseInt(m[1]!, 16);
            const type = parseInt(m[2]!, 16);
            map.set(tag, { entry: e, type });
        }
        return map;
    }

    // Prefer the proper Red-Black tree walk under the root entry — that's
    // how real MSG files structure their dir tree and how we tell which
    // property streams belong to the message vs. its attachments. But
    // some hand-built / test fixtures store all entries flat with no
    // child links at all (root.child === NOSTREAM); in that case fall
    // back to scanning every dir entry for top-level property streams,
    // matching the parser's pre-attachment-support behaviour.
    let topLevelChildren: (DirEntry | null)[] = listChildrenOf(rootEntry);
    if (topLevelChildren.length === 0) {
        topLevelChildren = entries.filter(e => e && e !== rootEntry);
    }
    const propStreams = buildPropMapFromList(topLevelChildren);

    // `data[i]` reads below are loop-bounded (i < data.length), so the `!`
    // assertions hold and are compile-erased.
    function decodeStreamString(prop: PropStream | undefined): string | null {
        if (!prop) return null;
        const data = readEntryData(prop.entry);
        if (!data || data.length === 0) return null;

        if (prop.type === PT_UNICODE) {
            let str = '';
            for (let i = 0; i < data.length - 1; i += 2) {
                const code = data[i]! | (data[i + 1]! << 8);
                if (code === 0) break;
                str += String.fromCharCode(code);
            }
            return str.trim();
        }
        if (prop.type === PT_STRING8) {
            let str = '';
            for (let i = 0; i < data.length; i++) {
                if (data[i] === 0) break;
                str += String.fromCharCode(data[i]!);
            }
            return str.trim();
        }
        if (prop.type === PT_BINARY) {
            try {
                return new TextDecoder('utf-8', { fatal: false }).decode(data).trim();
            } catch {
                let str = '';
                for (let i = 0; i < data.length; i++) str += String.fromCharCode(data[i]!);
                return str.trim();
            }
        }
        return null;
    }

    function decodeStreamBytes(prop: PropStream | undefined): Uint8Array | null {
        if (!prop) return null;
        return readEntryData(prop.entry);
    }

    function getPropString(tag: number, map: Map<number, PropStream> = propStreams): string | null {
        return decodeStreamString(map.get(tag));
    }

    // ── Top-level message headers ────────────────────────────────────
    const headers: MsgHeaders = {};

    const subject = getPropString(PROP_TAGS.SUBJECT);
    if (subject) headers.emailSubject = subject;

    const senderName  = getPropString(PROP_TAGS.SENDER_NAME)
                     || getPropString(PROP_TAGS.SENT_REPRESENTING);

    // v5.14.42: prefer the SMTP-flavour properties FIRST. Exchange fills
    // PR_SENDER_EMAIL_ADDRESS (0x0C1F) with the LDAP DN
    // (`/O=ExchangeLabs/OU=…/CN=…`) for messages sent through the
    // Exchange transport — that's a globally unique recipient identifier,
    // not a friendly SMTP address. PR_SENDER_SMTP_ADDRESS (0x5D01) and
    // PR_SENT_REPRESENTING_SMTP_ADDRESS (0x5D02) carry the actual SMTP
    // address. Try those first; only fall back to the LDAP-DN-flavoured
    // properties when no SMTP variant is present.
    let senderEmail = getPropString(PROP_TAGS.SENDER_SMTP)
                   || getPropString(PROP_TAGS.SENT_REP_SMTP)
                   || getPropString(PROP_TAGS.SENDER_EMAIL)
                   || getPropString(PROP_TAGS.SENT_REP_EMAIL);

    // If what we got still looks like an LDAP DN, try to extract the
    // real SMTP address from the transport-headers `From:` line below.
    if (senderName && senderEmail) {
        headers.emailFrom = `${senderName} <${senderEmail}>`;
    } else {
        headers.emailFrom = senderName || senderEmail || null;
    }

    const displayTo = getPropString(PROP_TAGS.DISPLAY_TO);
    if (displayTo) headers.emailTo = displayTo;

    const displayCc = getPropString(PROP_TAGS.DISPLAY_CC);
    if (displayCc) headers.emailCc = displayCc;

    // Date — try the transport-headers stream (0x007D) first, then the
    // PR_CLIENT_SUBMIT_TIME / PR_MESSAGE_DELIVERY_TIME FILETIME values
    // from the property table at the head of the __properties_version1.0
    // stream (most MSG files store the submit time there).
    // v5.14.42: also use transport headers to REPAIR a sender that came
    // back with an LDAP DN (`/O=...` / `/CN=...`) instead of a real SMTP
    // address. The transport `From:` line carries the real SMTP envelope.
    function looksLikeLdapDn(s: unknown): s is string {
        return typeof s === 'string' && /\/O=|\/OU=|\/CN=/.test(s);
    }
    const transportHeaders = getPropString(0x007D);
    if (transportHeaders) {
        const dateMatch = transportHeaders.match(/^Date:\s*(.+)$/im);
        if (dateMatch) headers.emailDate = dateMatch[1]!.trim();
        const fromMatch = transportHeaders.match(/^From:\s*(.+)$/im);
        const fromHeader = fromMatch ? fromMatch[1]!.trim() : null;
        if (fromHeader && (!headers.emailFrom || looksLikeLdapDn(headers.emailFrom))) {
            // Prefer the transport From header — it always has a real SMTP.
            headers.emailFrom = fromHeader;
        }
        if (!headers.emailTo) {
            const toMatch = transportHeaders.match(/^To:\s*(.+)$/im);
            if (toMatch) headers.emailTo = toMatch[1]!.trim();
        }
    }
    // Final scrub: if emailFrom STILL looks like an LDAP DN (rare, when
    // the message has no transport headers at all — typical for drafts),
    // strip the DN out and fall back to just the sender name.
    if (looksLikeLdapDn(headers.emailFrom)) {
        const m = headers.emailFrom.match(/^([^<]+?)\s*</);
        if (m) headers.emailFrom = m[1]!.trim();
    }
    if (!headers.emailDate) {
        const filetime = readDateFromPropTable(topLevelChildren, [PROP_TAGS.CLIENT_SUBMIT_TIME, PROP_TAGS.MESSAGE_DELIVERY]);
        if (filetime) headers.emailDate = filetime.toISOString();
    }

    // ── Body (plain + html as separate strings) ──────────────────────
    const bodyPlain = getPropString(PROP_TAGS.BODY) || '';
    const bodyHtml  = getPropString(PROP_TAGS.BODY_HTML) || '';

    // Backward-compat single-string `body` for legacy callers
    // (preprocessing-pipeline.js etc): prefer plain, fall back to a
    // tag-stripped version of the HTML.
    const body = (bodyPlain || htmlToPlainText(bodyHtml) || '').substring(0, 50000);

    // ── Attachments (walk every __attach_versionX_#XXXXXXXX storage) ─
    const attachments: MsgAttachment[] = [];
    for (const e of topLevelChildren) {
        if (!e || e.type !== 1) continue;
        if (!/^__attach_version\d+\.\d+_#[0-9A-Fa-f]+$/i.test(e.name)) continue;
        if (attachments.length >= MAX_ATTACHMENTS) break;

        const attachChildren = listChildrenOf(e);
        const attachMap = buildPropMapFromList(attachChildren);

        const longName  = getPropString(PROP_TAGS.ATTACH_LONG_FILENAME, attachMap);
        const shortName = getPropString(PROP_TAGS.ATTACH_FILENAME, attachMap);
        const displayName = getPropString(PROP_TAGS.DISPLAY_NAME, attachMap);
        const ext = getPropString(PROP_TAGS.ATTACH_EXTENSION, attachMap) || '';
        const filename = longName || displayName || shortName || `attachment-${attachments.length + 1}${ext}`;

        const mimeTagRaw = getPropString(PROP_TAGS.ATTACH_MIME_TAG, attachMap) || '';
        const contentId = (getPropString(PROP_TAGS.ATTACH_CONTENT_ID, attachMap) || '').replace(/^<|>$/g, '');

        const dataProp = attachMap.get(PROP_TAGS.ATTACH_DATA_BIN);
        const data = dataProp ? decodeStreamBytes(dataProp) : null;
        if (!data || data.length === 0) continue;

        const mimeType = mimeTagRaw || guessMimeFromName(filename) || 'application/octet-stream';
        attachments.push({
            filename,
            mimeType,
            size: data.length,
            isInline: !!contentId,
            contentId,
            dataUrl: bytesToDataUrl(data, mimeType),
            // `data` is an ArrayBuffer-backed Uint8Array at runtime; the cast
            // satisfies BlobPart (lib widens to Uint8Array<ArrayBufferLike>)
            // and is compile-erased.
            blob: typeof Blob !== 'undefined' ? new Blob([data as BlobPart], { type: mimeType }) : null,
        });
    }

    // Remap cid: references inside HTML body to data: URLs (if both exist)
    const remappedHtml = bodyHtml ? remapCidLinks(bodyHtml, attachments) : '';

    return {
        headers,
        body,                  // legacy single-string body (plain pref, html-stripped fallback)
        bodyPlain,             // raw plain part
        bodyHtml: remappedHtml || bodyHtml,  // HTML with inline-image cids resolved to data URLs
        attachments,
    };
}

// ─── Property-table FILETIME reader ────────────────────────────────────────
// MAPI fixed-length properties (FILETIMEs, ints, bools) live in the
// __properties_version1.0 stream as 16-byte records. For top-level message
// storage the header is 32 bytes; for embedded msg/attachment storage it's
// 24 bytes; for recipient/attachment object storages it's 8 bytes. We read
// the message-level layout (32-byte preamble, then 16-byte records) and
// pick out the FILETIME for the requested tag.
// NOTE (honest-typing): this is a preserved no-op. It always returns null
// regardless of input (the real FILETIME read happens inline in parseMsgBinary);
// the stub is kept for backward compat. `tagsInPriorityOrder` is intentionally
// unused. Behaviour is unchanged by the TS conversion. Return type is
// `Date | null` because the (currently dead) caller calls `.toISOString()`.
function readDateFromPropTable(children: (DirEntry | null)[], tagsInPriorityOrder: number[]): Date | null {
    const propsEntry = children.find(c => c && c.name === '__properties_version1.0');
    if (!propsEntry) return null;
    return null; // requires readEntryData closure — done inline above; kept here as no-op for backward compat
}

// ─── Helpers shared with EML parser surface ────────────────────────────────

function htmlToPlainText(html: string): string {
    if (!html) return '';
    return html
        .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
        .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>/gi, '\n\n')
        .replace(/<\/div>/gi, '\n')
        .replace(/<\/tr>/gi, '\n')
        .replace(/<\/li>/gi, '\n')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}

function bytesToDataUrl(bytes: Uint8Array, mimeType: string): string {
    if (!bytes || bytes.length === 0) return '';
    let binary = '';
    const CHUNK = 0x8000;
    for (let i = 0; i < bytes.length; i += CHUNK) {
        binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK) as unknown as number[]);
    }
    try {
        return `data:${mimeType};base64,${btoa(binary)}`;
    } catch {
        return '';
    }
}

function guessMimeFromName(name: string): string {
    const m = (name || '').toLowerCase().match(/\.([a-z0-9]+)$/);
    if (!m) return '';
    const mimeMap: Record<string, string> = {
        jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif',
        webp: 'image/webp', heic: 'image/heic', svg: 'image/svg+xml',
        pdf: 'application/pdf', zip: 'application/zip',
        doc: 'application/msword',
        docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        xls: 'application/vnd.ms-excel',
        xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        ppt: 'application/vnd.ms-powerpoint',
        pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        txt: 'text/plain', csv: 'text/csv', html: 'text/html', htm: 'text/html',
        eml: 'message/rfc822', msg: 'application/vnd.ms-outlook',
    };
    return mimeMap[m[1]!] || '';
}

function remapCidLinks(html: string, attachments: MsgAttachment[]): string {
    if (!html || !attachments?.length) return html;
    return html
        .replace(/(["'])cid:([^"'>\s]+)\1/gi, (full: string, q: string, cid: string) => {
            const cleanCid = decodeURIComponent(cid).trim();
            const match = attachments.find(a => a.contentId === cleanCid);
            return (match && match.dataUrl) ? `${q}${match.dataUrl}${q}` : full;
        })
        .replace(/\bsrc\s*=\s*cid:([^"'>\s]+)/gi, (full: string, cid: string) => {
            const cleanCid = decodeURIComponent(cid).trim();
            const match = attachments.find(a => a.contentId === cleanCid);
            return (match && match.dataUrl) ? `src="${match.dataUrl}"` : full;
        });
}
